import { useCallback, useEffect, useMemo, useRef, useState, VoidFunctionComponent } from 'react';
import { OnlyBotMaterialPreset } from '@animavirtuality/onlybots-three';
import { useKey } from 'react-use';
import { Color, Group, MathUtils, Vector3 } from 'three';
import { GradientTexture, PerspectiveCamera } from '@react-three/drei';
import { ethers } from 'ethers';
import { useProvider } from 'wagmi';
import { useFrame } from '@react-three/fiber';
import { useErrorHandler } from 'react-error-boundary';
import { OnlyBot } from '@animavirtuality/onlybots-core';
import { Address, BOT_LAYER_IDS, IPFS_MIRROR, tokenContractAddress } from '@/lib/globals';
import OnlyBotsToken from '@/contracts/OnlyBotsToken.json';
import { Metadata } from '@/lib/types';
import { useCollectionInfo } from '@/hooks/useCollectionInfo';
import { PortalContent } from '@/components/scenes/edition/PortalContents';
import { OutsidePortal } from '@/components/scenes/edition/OutsidePortal';
import { useResponsive } from '@/hooks/useResponsive';
import { EditionCard } from '@/components/scenes/edition/EditionCard';
import { getGltfUri, getQrTextureUri } from '@/lib/ipfsMirror';
import { deviceProperties } from '@/lib/deviceProperties';
import { useLerpedMouse } from '@/hooks/useLerpedMouse';
import { useDeviceOrientation } from '@/hooks/useDeviceOrientation';
import { WrappedError } from '@/lib/WrappedError';
import { convertShaderToPreset, presets } from '@/lib/presets';
import { useRouterContext } from '@/context/RouterContext';
import { bgBlack, getGradientColorsFromBot } from '@/lib/colors';

const initialPosition = new Vector3(0, 0, 5);
const target = new Vector3(0, 0, -1);

const getColor = (typeID: number, bot: OnlyBot): Color => {
    const layer = bot.layers.find(({ type }) => type === typeID);
    if (layer && bot.materials.length > layer.material) {
        const material = bot.materials[layer.material];
        if (material) {
            const [r, g, b] = material.color;
            return new Color(r / 255, g / 255, b / 255).convertSRGBToLinear();
        }
    }
    return new Color(0xffffff).convertSRGBToLinear();
};

interface Attribute {
    trait_type: string;
    value: string;
}

const getMaterial = (
    typeID: number,
    attributes: Attribute[],
    bot: OnlyBot
): { name: string; material?: OnlyBotMaterialPreset } => {
    const layer = bot.layers.find(({ type }) => type === typeID);
    if (layer) {
        const { preset } = bot.materials[layer.material];
        const material = presets[preset];
        const materialAttributes = attributes.filter((attribute) => attribute.trait_type === 'Material');
        if (materialAttributes.length > layer.material) {
            return {
                name: materialAttributes[layer.material].value,
                material,
            };
        }
    }
    return {
        name: '',
    };
};

export const CARD_WIDTH = 2.44;
export const CARD_ASPECT = 0.72;
export const CARD_HEIGHT = CARD_WIDTH / CARD_ASPECT;

const IPFS = 'ipfs://';
export const Edition: VoidFunctionComponent = () => {
    const errorHandler = useErrorHandler();
    const { tokenId } = useRouterContext();
    const provider = useProvider();
    const { totalSupply } = useCollectionInfo();
    const [bot, setBot] = useState<OnlyBot>();
    const [botMetadata, setBotMetadata] = useState<Metadata>();
    const [botQrUrl, setBotQrUrl] = useState<string>('');
    const [gltfUrl, setGltfUrl] = useState<string>('');
    const [tokenOwner, setTokenOwner] = useState<string>('');
    const { scale, responsiveValue } = useResponsive();
    const { navigate } = useRouterContext();
    const { isTouchDevice, supportsOrientation } = deviceProperties();
    const mouse = useLerpedMouse();
    const { orientation, reOrient } = useDeviceOrientation();
    const legendary = botMetadata?.class?.toLocaleLowerCase() === 'legendary';
    const groupRef = useRef<Group>(new Group());
    useKey('ArrowRight', () => navigate((tokenId + 1).toString(10)));
    useKey('ArrowLeft', () => navigate((tokenId - 1).toString(10)));

    const getBotData = useCallback(async () => {
        if (tokenId && totalSupply) {
            try {
                const contract = new ethers.Contract(tokenContractAddress, OnlyBotsToken.abi, provider);
                setBot(OnlyBot.fromJSON(convertShaderToPreset(JSON.parse(await contract.getBotData(tokenId)))));
            } catch (e: any) {
                console.error('error getting bot:', e);
                errorHandler(new WrappedError(`Error in contract.getBotData(${tokenId.toString(10)})`, e));
            }
        }
    }, [provider, tokenId, totalSupply, errorHandler]);
    const getBotOwner = useCallback(async () => {
        if (tokenId && totalSupply) {
            try {
                const contract = new ethers.Contract(tokenContractAddress, OnlyBotsToken.abi, provider);
                const owner = await contract.ownerOf(tokenId);
                setTokenOwner(owner);
            } catch (e: any) {
                console.error(e);
            }
        }
    }, [provider, setTokenOwner, tokenId, totalSupply]);

    const getBotMetadata = useCallback(async () => {
        if (tokenId && totalSupply) {
            try {
                const contract = new ethers.Contract(tokenContractAddress, OnlyBotsToken.abi, provider);
                const tokenURI = await contract.tokenURI(tokenId);
                if (tokenURI.startsWith(IPFS)) {
                    const path = tokenURI.slice(IPFS.length);
                    const tokenJsonURI = new URL(`${IPFS_MIRROR}${path}`);
                    const response = await fetch(tokenJsonURI);
                    const {
                        name,
                        attributes,
                        assets,
                        ext: { source: bot },
                    } = JSON.parse(await response.text());
                    const traits = attributes.reduce(
                        (accum: { [x: string]: string | number | boolean }, { trait_type, value }: any) => {
                            accum[trait_type.toLowerCase()] = value;
                            return accum;
                        },
                        {}
                    );
                    const bodyColor = getColor(BOT_LAYER_IDS.body, bot);
                    const eyeColor = getColor(BOT_LAYER_IDS.eye, bot);
                    const bodyMaterial = getMaterial(BOT_LAYER_IDS.body, attributes, bot);
                    setBotMetadata({
                        ...traits,
                        name,
                        bodyColor,
                        eyeColor,
                        bodyMaterialName: bodyMaterial.name,
                        preset: bodyMaterial.material || {},
                    });
                    setBotQrUrl(getQrTextureUri(assets));
                    setGltfUrl(getGltfUri(assets));
                }
            } catch (e: any) {
                console.error(e);
            }
        }
    }, [provider, tokenId, totalSupply]);

    const backgroundColors: [[string, string], [string, string]] = useMemo(() => {
        if (botMetadata?.eyeColor && botMetadata?.bodyColor) {
            return getGradientColorsFromBot(botMetadata.bodyColor.clone(), botMetadata.eyeColor.clone(), legendary);
        }
        return [
            [bgBlack, bgBlack],
            [bgBlack, bgBlack],
        ];
    }, [botMetadata?.bodyColor, botMetadata?.eyeColor, legendary]);

    // if tokenID is greater than supply, send to not-found
    useEffect(() => {
        if (tokenId && totalSupply) {
            if (tokenId <= totalSupply) {
                void getBotData();
                void getBotMetadata();
                void getBotOwner();
            } else {
                navigate('/not-found');
            }
        }
    }, [tokenId, totalSupply]); // eslint-disable-line react-hooks/exhaustive-deps

    useFrame(({ camera }) => {
        if (orientation.current && orientation.current.beta !== null && orientation.current.gamma !== null) {
            camera.position.set(
                MathUtils.lerp(0, 10, orientation.current.gamma / 270),
                MathUtils.lerp(0, 5, -orientation.current.beta / 135),
                initialPosition.z
            );
            camera.lookAt(target);
        } else {
            groupRef.current.rotation.set(
                MathUtils.lerp(0, Math.PI / 18, mouse.current.y),
                MathUtils.lerp(0, Math.PI / 6, mouse.current.x),
                0
            );
        }
    });

    return (
        <group onClick={reOrient} position={[0, responsiveValue(0, -0.15), 0]}>
            <directionalLight layers={0} intensity={0.5} color="#fff" position={[-1, 40, 40]} />
            <PerspectiveCamera fov={75} far={2000} position={initialPosition} makeDefault>
                <group position={[0, 0, -5]} scale={scale}>
                    {tokenId && tokenOwner && (
                        <OutsidePortal
                            qrUrl={botQrUrl}
                            tokenOwner={tokenOwner as Address}
                            gltfUrl={gltfUrl}
                            name={botMetadata?.name || ''}
                            tokenId={tokenId}
                        />
                    )}
                    {isTouchDevice && supportsOrientation && bot && botMetadata && tokenId && (
                        <EditionCard
                            botMetadata={botMetadata}
                            tokenId={tokenId}
                            width={CARD_WIDTH}
                            height={CARD_HEIGHT}
                        />
                    )}
                </group>
                <mesh position={[0, 0, -20]}>
                    <planeGeometry args={[500, 100]} />
                    <meshBasicMaterial>
                        <GradientTexture stops={[0, 1]} colors={backgroundColors[0]} />
                    </meshBasicMaterial>
                </mesh>
            </PerspectiveCamera>

            <group ref={groupRef} scale={scale}>
                {!isTouchDevice && !supportsOrientation && bot && botMetadata && tokenId && (
                    <EditionCard botMetadata={botMetadata} tokenId={tokenId} width={CARD_WIDTH} height={CARD_HEIGHT} />
                )}
                {bot && botMetadata && tokenId && (
                    <PortalContent gradientColors={backgroundColors[1]} legendary={legendary} bot={bot} />
                )}
            </group>
        </group>
    );
};
